import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Close as CloseIcon,
  GraphicEq as WaveIcon,
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import axios from 'axios';

const NOTE_TYPES = [
  { value: 'pm_note', label: 'PM Note', color: '#2E7D32', icon: <EngineeringIcon fontSize="small" /> },
  { value: 'tech_note', label: 'Tech Note', color: '#1565C0', icon: <BuildIcon fontSize="small" /> },
  { value: 'aftercare_note', label: 'AfterCare', color: '#FF8F00', icon: <SupportIcon fontSize="small" /> },
];

// Auto-detect note type from user role
function defaultNoteType(userRole) {
  if (!userRole) return 'pm_note';
  const r = userRole.toLowerCase();
  if (r.includes('aftercare') || r.includes('service')) return 'aftercare_note';
  if (r.includes('tech') || r.includes('install') || r.includes('field')) return 'tech_note';
  return 'pm_note';
}

// Animated pulsing dot for recording state
function PulsingDot() {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        bgcolor: 'error.main',
        boxShadow: '0 0 0 0 rgba(211,47,47,0.7)',
        animation: 'pulse 1.4s ease-in-out infinite',
        '@keyframes pulse': {
          '0%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.7)' },
          '70%': { boxShadow: '0 0 0 14px rgba(211,47,47,0)' },
          '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
        },
      }}
    />
  );
}

// Format seconds as mm:ss
function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * VoiceNote — record a voice note, transcribe it, and save it as an activity_log entry.
 *
 * Props:
 *   projectId  — numeric project ID
 *   onSaved    — callback(noteEntry) called after successful save so parent can prepend the note
 */
export default function VoiceNote({ projectId, onSaved, userRole }) {
  const [open, setOpen] = useState(false);
  // 'idle' | 'recording' | 'transcribing' | 'done' | 'error'
  const [phase, setPhase] = useState('idle');
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [savedNote, setSavedNote] = useState(null);
  const [noteType, setNoteType] = useState(() => defaultNoteType(userRole));

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const resetState = useCallback(() => {
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsedSecs(0);
    setPhase('idle');
    setErrorMsg('');
    setSavedNote(null);
  }, []);

  const handleOpen = () => {
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const startRecording = async () => {
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer webm, fall back to whatever is supported
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
        ? 'audio/ogg;codecs=opus'
        : '';

      const options = mimeType ? { mimeType } : {};
      const mr = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mimeType || 'audio/webm',
        });
        sendForTranscription(blob, mimeType || 'audio/webm');
      };

      mr.start(250); // collect chunks every 250 ms for reliable mobile recording

      setPhase('recording');
      setElapsedSecs(0);
      timerRef.current = setInterval(() => setElapsedSecs(s => s + 1), 1000);
    } catch (err) {
      console.error('Microphone error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMsg('Microphone access denied. Please allow microphone permission in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setErrorMsg('No microphone found on this device.');
      } else {
        setErrorMsg('Could not access microphone: ' + err.message);
      }
      setPhase('error');
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      setPhase('transcribing');
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const sendForTranscription = async (blob, mimeType) => {
    try {
      const ext = mimeType.includes('wav') ? 'wav' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      const formData = new FormData();
      formData.append('audio', blob, `voice_note.${ext}`);
      formData.append('note_type', noteType);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/projects/${projectId}/voice-note`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        }
      );

      setSavedNote(response.data);
      setPhase('done');
      if (onSaved) onSaved(response.data);
    } catch (err) {
      console.error('Voice note transcription error:', err);
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Transcription failed. Please try again.';
      setErrorMsg(msg);
      setPhase('error');
    }
  };

  return (
    <>
      {/* Trigger button — prominent mic button, easy to tap on mobile */}
      <Button
        variant="contained"
        color="error"
        startIcon={<MicIcon />}
        onClick={handleOpen}
        size="large"
        sx={{
          borderRadius: 3,
          px: 3,
          py: 1.5,
          fontWeight: 700,
          fontSize: '1rem',
          minWidth: 160,
          boxShadow: 3,
          '&:hover': { boxShadow: 6 },
        }}
      >
        Record Note
      </Button>

      {/* Modal */}
      <Dialog
        open={open}
        onClose={phase === 'recording' ? undefined : handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            mx: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MicIcon color="error" />
            <Typography variant="h6" fontWeight={700}>
              Voice Note
            </Typography>
          </Box>
          {phase !== 'recording' && (
            <IconButton onClick={handleClose} size="small" edge="end" aria-label="close">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent>
          {/* IDLE — prompt to start */}
          {phase === 'idle' && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              {/* Note type selector */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                What type of note?
              </Typography>
              <ToggleButtonGroup
                value={noteType}
                exclusive
                onChange={(e, v) => { if (v) setNoteType(v); }}
                sx={{ mb: 3 }}
                size="large"
              >
                {NOTE_TYPES.map((nt) => (
                  <ToggleButton
                    key={nt.value}
                    value={nt.value}
                    sx={{
                      px: 2.5, py: 1.5, gap: 1,
                      '&.Mui-selected': {
                        bgcolor: `${nt.color}15`,
                        color: nt.color,
                        borderColor: nt.color,
                        '&:hover': { bgcolor: `${nt.color}25` },
                      },
                    }}
                  >
                    {nt.icon}
                    <Typography variant="body2" fontWeight={600}>{nt.label}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Box sx={{ mb: 2 }}>
                <MicIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Tap record to start. Your voice will be transcribed and saved.
              </Typography>
            </Box>
          )}

          {/* RECORDING */}
          {phase === 'recording' && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
                <PulsingDot />
                <Typography variant="body2" color="error" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Recording
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', mb: 1 }}>
                {formatTime(elapsedSecs)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <WaveIcon sx={{ fontSize: 40, color: 'error.light', animation: 'wave 0.8s ease-in-out infinite alternate', '@keyframes wave': { from: { transform: 'scaleY(1)' }, to: { transform: 'scaleY(1.4)' } } }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Speak clearly — tap Stop when finished.
              </Typography>
            </Box>
          )}

          {/* TRANSCRIBING */}
          {phase === 'transcribing' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={56} sx={{ mb: 2 }} />
              <Typography variant="body1" fontWeight={600} gutterBottom>
                Transcribing…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This usually takes a few seconds.
              </Typography>
            </Box>
          )}

          {/* DONE */}
          {phase === 'done' && savedNote && (
            <Box sx={{ py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Note saved successfully!
              </Alert>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label="Voice Note" size="small" color="error" variant="outlined" icon={<MicIcon />} />
                  <Typography variant="caption" color="text.secondary">
                    {savedNote.user_name}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {savedNote.details}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ERROR */}
          {phase === 'error' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg || 'Something went wrong. Please try again.'}
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
          {phase === 'idle' && (
            <Button
              variant="contained"
              color="error"
              startIcon={<MicIcon />}
              onClick={startRecording}
              size="large"
              fullWidth
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: '1.1rem' }}
            >
              Start Recording
            </Button>
          )}

          {phase === 'recording' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<StopIcon />}
              onClick={stopRecording}
              size="large"
              fullWidth
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: '1.1rem' }}
            >
              Stop &amp; Transcribe
            </Button>
          )}

          {phase === 'done' && (
            <Button
              variant="outlined"
              onClick={handleClose}
              size="large"
              fullWidth
              sx={{ borderRadius: 2, py: 1.5 }}
            >
              Close
            </Button>
          )}

          {phase === 'error' && (
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button
                variant="outlined"
                onClick={resetState}
                size="large"
                sx={{ flex: 1, borderRadius: 2, py: 1.5 }}
              >
                Try Again
              </Button>
              <Button
                variant="text"
                onClick={handleClose}
                size="large"
                sx={{ flex: 1, borderRadius: 2, py: 1.5 }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
